/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * EmergencyContactsScreen — onboarding "circle of trust" step (after profile,
 * before friend discovery). Adds up to 3 emergency contacts, each with a
 * RELATION (preset chip or free text). Relation only — no proximity / location
 * / tier semantics.
 *
 * Each added contact is written to BOTH:
 *   (a) /users/{uid}/emergencyContacts — via useEmergencyContacts.addContact
 *       (which owns the max-3 enforcement), with the relation in the record.
 *   (b) /users/{uid}/safeCircle       — via useSafeCircleStaticMembers.addMember
 *       (the existing "Static" circle-member write path), mapping our relation
 *       string straight onto its free-text `relation` field.
 * No new RTDB paths; both are self-scoped writes the existing rules already
 * permit. Phone numbers are normalized to E.164 before either write.
 *
 * The whole step is skippable ("I'll do this later") — emergency contacts are
 * optional. There is one subtle micro-celebration when a contact is added; it
 * is non-essential (the screen works if the burst is removed).
 */

import { useCallback, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { motion, useReducedMotion } from "framer-motion";
import { Plus, ShieldCheck, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/contexts/AuthContext";
import { useEmergencyContacts } from "@/hooks/useEmergencyContacts";
import { useSafeCircleStaticMembers } from "@/hooks/useSafeCircleStaticMembers";
import { detectCountryFromInput, normalizePhone } from "@/lib/phone";
import { regionFromLocale } from "@/utils/phone";
import {
  CUSTOM_RELATION_LABEL,
  PRESET_RELATIONS,
  resolveRelation,
} from "@/utils/emergencyRelations";
import OnboardingShell from "@/components/onboarding/OnboardingShell";
import { pickEmergencyContact } from "@/utils/contactsPicker";
import PhoneCountryField from "@/components/shared/PhoneCountryField";
import type { CountryCode } from "libphonenumber-js";

interface Props {
  onContinue: () => void;
  onBack?: () => void;
  step: number;
  totalSteps: number;
}

const MAX = 3;

// A bare national number ("9876543210") needs a region to resolve correctly
// — falls back to US only when the device locale carries no usable region.
const DEVICE_DEFAULT_COUNTRY = regionFromLocale(navigator.language) ?? "US";

type AddStage = "closed" | "entry" | "relation";

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "?";
}

// Up-to-two initials for the centered "You" avatar (e.g. "Alex Rivera" → "AR").
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

// Fixed slot positions on ONE ring around the centered "You" avatar. Placement
// is purely by add-order INDEX → a fixed angle — NOT by location, distance, or
// any proximity/tier logic (deliberately dropped earlier). 3 slots evenly at
// 120°: top, lower-left, lower-right. Percentages are within the square ring
// container; {x,y} center each slot via translate(-50%,-50%).
const SLOT_POSITIONS = [
  { top: "6%", left: "50%" }, // index 0 — top
  { top: "78%", left: "16%" }, // index 1 — lower-left
  { top: "78%", left: "84%" }, // index 2 — lower-right
] as const;

// Confetti micro-burst offsets (purely decorative, framer-motion driven). Fixed
// vectors so there's no Math.random (kept deterministic + lint-clean).
const CONFETTI = [
  { x: -34, y: -40, c: "#7C52F5" },
  { x: 30, y: -44, c: "#B79CFb".toUpperCase() },
  { x: 44, y: 6, c: "#9B7BF7" },
  { x: -42, y: 8, c: "#C9B8F9" },
  { x: -16, y: 46, c: "#7C52F5" },
  { x: 20, y: 44, c: "#B79CF8" },
] as const;

export default function EmergencyContactsScreen({
  onContinue,
  onBack,
  step,
  totalSteps,
}: Props) {
  const { user } = useAuth();
  const { contacts, addContact } = useEmergencyContacts();
  const { addMember } = useSafeCircleStaticMembers();

  const [stage, setStage] = useState<AddStage>("closed");
  const [name, setName] = useState("");
  const [country, setCountry] = useState<CountryCode>(DEVICE_DEFAULT_COUNTRY);
  const [phone, setPhone] = useState("");
  const [relationChoice, setRelationChoice] = useState<string | null>(null);
  const [customRelation, setCustomRelation] = useState("");
  const [saving, setSaving] = useState(false);
  const [celebrateId, setCelebrateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const count = contacts.length;
  const full = count >= MAX;
  const isNative = Capacitor.isNativePlatform();
  const reduceMotion = useReducedMotion();

  // Centered "You" avatar initials from the signed-in display name.
  const youInitials = initialsOf(user?.displayName ?? "");

  const resetAddForm = useCallback(() => {
    setName("");
    setCountry(DEVICE_DEFAULT_COUNTRY);
    setPhone("");
    setRelationChoice(null);
    setCustomRelation("");
    setError(null);
  }, []);

  const openAdd = useCallback(() => {
    resetAddForm();
    setStage("entry");
  }, [resetAddForm]);

  const closeAdd = useCallback(() => {
    setStage("closed");
    resetAddForm();
  }, [resetAddForm]);

  const pickFromContacts = useCallback(async () => {
    const result = await pickEmergencyContact();
    switch (result.status) {
      case "selected":
        setName(result.name);
        setCountry(detectCountryFromInput(result.phone) ?? DEVICE_DEFAULT_COUNTRY);
        setPhone(result.phone);
        break;
      case "multi-number": {
        // Take the first number; the user can edit it before continuing.
        const picked = result.numbers[0] ?? "";
        setName(result.name);
        setCountry(detectCountryFromInput(picked) ?? DEVICE_DEFAULT_COUNTRY);
        setPhone(picked);
        break;
      }
      case "no-phone":
        toast.error("That contact has no phone number.");
        break;
      case "denied":
        toast.error("Contacts permission denied. Enter the details manually.");
        break;
      case "cancelled":
        break;
      case "unsupported":
      case "error":
      default:
        // Manual entry is always available below — nothing to surface.
        break;
    }
  }, []);

  const goToRelation = useCallback(() => {
    if (!name.trim()) {
      setError("Add a name.");
      return;
    }
    const result = normalizePhone(phone, country);
    if ("error" in result) {
      setError("Enter a valid phone number.");
      return;
    }
    setError(null);
    setStage("relation");
  }, [name, phone, country]);

  const confirmAdd = useCallback(async () => {
    const trimmedName = name.trim();
    const result = normalizePhone(phone, country);
    if (!trimmedName || "error" in result) {
      setStage("entry");
      return;
    }
    const e164 = result.e164;
    const relation = resolveRelation(relationChoice, customRelation);
    setSaving(true);
    setError(null);
    try {
      // (a) Emergency contacts (owns max-3 enforcement). Store E.164.
      const added = addContact(trimmedName, e164, relation ?? undefined);
      if (!added) {
        toast.error("You can add up to 3 contacts.");
        setSaving(false);
        return;
      }

      // (b) SafeCircle member — same relation string on the free-text field.
      // Best-effort: a failure here shouldn't roll back the emergency contact
      // (both are independent self-writes); surface it but keep going.
      try {
        await addMember({
          displayName: trimmedName,
          phoneNumber: e164,
          relation: relation ?? undefined,
        });
      } catch (err) {
        // Observability only (item 5 — not a retry engine): log the failed
        // SafeCircle leg of the dual-write with enough context (uid + a stable
        // contact identifier) to measure how often the emergencyContacts and
        // safeCircle paths diverge. The emergency contact itself was already
        // written, so this is a partial, recoverable desync — a future batch
        // owns reconciliation.
        console.error("[EmergencyContactsScreen] SafeCircle write failed:", err);
        Sentry.captureException(err, {
          tags: {
            area: "onboarding.emergency.safecircle",
            issue: "dual_write_partial_failure",
          },
          extra: {
            uid: user?.uid ?? null,
            // E.164 is the stable contact identifier shared by both writes
            // (the emergencyContacts `ec_…` id isn't surfaced by addContact).
            contactE164: e164,
            contactName: trimmedName,
          },
        });
        toast("Saved to your contacts. We'll sync your circle shortly.");
      }

      // Micro-celebration — non-essential delight.
      setCelebrateId(`ec_celebrate_${count}`);
      window.setTimeout(() => setCelebrateId(null), 1200);

      closeAdd();
    } catch (err) {
      console.error("[EmergencyContactsScreen] add failed:", err);
      Sentry.captureException(err, { tags: { area: "onboarding.emergency" } });
      setError("Couldn't save. Check your connection and retry.");
    } finally {
      setSaving(false);
    }
  }, [name, phone, country, relationChoice, customRelation, addContact, addMember, count, closeAdd, user]);

  return (
    <OnboardingShell
      step={step}
      totalSteps={totalSteps}
      onBack={onBack}
      ariaLabel="Your circle of trust"
      // While the add-contact modal is open it owns the action buttons
      // (Next / Back+Add). Hiding the shell's sticky "Continue" + secondary
      // avoids two near-identical purple full-width buttons competing at the
      // bottom of the sheet. When both labels are undefined OnboardingShell
      // omits its whole CTA block, so only the modal's button shows.
      ctaLabel={stage === "closed" ? "Continue" : undefined}
      onCta={onContinue}
      secondaryLabel={
        stage === "closed"
          ? count === 0
            ? "I'll do this later"
            : "Skip for now"
          : undefined
      }
      onSecondary={onContinue}
    >
      <div className="mx-auto flex max-w-md flex-col">
        <div className="flex flex-col items-center text-center">
          <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#7C52F5]/12 text-[#7C52F5] shadow-[0_12px_30px_-16px_rgba(124,82,245,0.7)]">
            <ShieldCheck size={30} aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-2xl font-bold tracking-tight text-[#2A2342]">
            Your circle of trust
          </h1>
          <p className="mt-2.5 text-[15px] leading-relaxed text-[#6B5FA0]">
            Pick up to 3 people we should reach if you ever need help. You can
            change this anytime.
          </p>
        </div>

        {/* ── Circle-of-trust ring ──────────────────────────────────────────
            "You" at the centre, your people on ONE ring around you — conveys
            "you're surrounded by your people". PRESENTATION ONLY: each slot is
            placed by add-order INDEX → a fixed angle (SLOT_POSITIONS), never by
            location / distance / proximity. Empty slots are the dashed "Add"
            affordance (ring-only) that opens the existing modal. */}
        <div
          className="relative mx-auto mt-8 aspect-square w-full max-w-[320px]"
          role="list"
          aria-label="Your circle of trust"
        >
          {/* Soft guide ring — purely decorative. */}
          <div
            aria-hidden="true"
            className="absolute inset-[14%] rounded-full border border-dashed border-[#D7CCF4]"
          />
          <div
            aria-hidden="true"
            className="absolute inset-[2%] rounded-full border border-[#E9E2F9]"
          />

          {/* Connector lines from "You" to each FILLED slot (decorative). */}
          <svg
            aria-hidden="true"
            viewBox="0 0 100 100"
            className="pointer-events-none absolute inset-0 h-full w-full"
          >
            {contacts.map((c, i) => {
              const pos = SLOT_POSITIONS[i];
              if (!pos) return null;
              return (
                <motion.line
                  key={c.id}
                  x1="50"
                  y1="50"
                  x2={parseFloat(pos.left)}
                  y2={parseFloat(pos.top)}
                  stroke="#C9B8F9"
                  strokeWidth="0.6"
                  strokeLinecap="round"
                  initial={reduceMotion ? false : { pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                />
              );
            })}
          </svg>

          {/* Centre "You" avatar with glow halo + breathing pulse. */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {/* Blurred glow halo. */}
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7C52F5]/25 blur-2xl"
            />
            {/* Breathing pulse ring (loops; static under reduced motion). */}
            {!reduceMotion && (
              <motion.span
                aria-hidden="true"
                className="absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#7C52F5]/40"
                initial={{ scale: 1, opacity: 0.5 }}
                animate={{ scale: [1, 1.55], opacity: [0.5, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
              />
            )}
            <div
              className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#8C63F8] to-[#B79CF8] text-xl font-bold text-white shadow-[0_18px_38px_-14px_rgba(124,82,245,0.9)] ring-4 ring-white"
              aria-label="You"
            >
              {youInitials || (
                <ShieldCheck className="h-7 w-7" aria-hidden="true" />
              )}
            </div>
            <span className="mt-1.5 block text-center text-xs font-semibold text-[#6B5FA0]">
              You
            </span>
          </div>

          {/* The 3 slots. */}
          {SLOT_POSITIONS.map((pos, i) => {
            const c = contacts[i];
            const isNewest =
              c != null &&
              celebrateId !== null &&
              c === contacts[contacts.length - 1];
            return (
              <div
                key={c?.id ?? `slot-${i}`}
                role="listitem"
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ top: pos.top, left: pos.left }}
              >
                {c ? (
                  <motion.div
                    className="relative flex flex-col items-center"
                    initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : { type: "spring", stiffness: 460, damping: 22 }
                    }
                  >
                    <span
                      className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#7C52F5] to-[#9B7BF7] text-lg font-bold text-white shadow-[0_12px_28px_-12px_rgba(124,82,245,0.85)] ring-4 ring-white"
                      aria-hidden="true"
                    >
                      {initialOf(c.name)}
                    </span>
                    {c.relation && (
                      <span className="mt-2 max-w-[7.5rem] truncate rounded-full bg-[#EDE7FB] px-3 py-1 text-xs font-semibold text-[#7C52F5]">
                        {c.relation}
                      </span>
                    )}
                    <span className="sr-only">{`${c.name}${c.relation ? `, ${c.relation}` : ""}`}</span>

                    {/* Confetti micro-burst on the most-recently-added slot. */}
                    {isNewest && !reduceMotion && (
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute left-8 top-8"
                      >
                        {CONFETTI.map((p, ci) => (
                          <motion.span
                            key={ci}
                            className="absolute h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: p.c }}
                            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                            animate={{ x: p.x, y: p.y, opacity: 0, scale: 0.4 }}
                            transition={{ duration: 0.9, ease: "easeOut" }}
                          />
                        ))}
                      </span>
                    )}
                  </motion.div>
                ) : (
                  <motion.button
                    type="button"
                    onClick={openAdd}
                    aria-label="Add a contact"
                    className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-dashed border-[#CFC3F0] bg-white/70 text-[#7C52F5] shadow-[0_8px_22px_-14px_rgba(124,82,245,0.6)] active:scale-95 transition-transform"
                    animate={
                      reduceMotion
                        ? undefined
                        : { scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] }
                    }
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Plus size={22} aria-hidden="true" />
                  </motion.button>
                )}
              </div>
            );
          })}
        </div>

        {/* Counter + affirming line. */}
        <p className="mt-6 text-center text-sm font-semibold text-[#2A2342]">
          {count} of {MAX} added
        </p>
        <p className="mt-1 text-center text-sm text-[#6B5FA0]">
          {full ? "You're surrounded 💜" : "Tap a circle to add someone you trust."}
        </p>
      </div>

      {/* Add modal — entry then relation. */}
      {stage !== "closed" && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add an emergency contact"
          className="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 sm:items-center"
        >
          <div className="w-full rounded-t-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-[#2A2342] shadow-xl sm:max-w-md sm:rounded-3xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold">
                {stage === "entry" ? "Add a contact" : "How do you know them?"}
              </h2>
              <button
                type="button"
                onClick={closeAdd}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full text-[#6B5FA0] active:scale-95"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {stage === "entry" && (
              <>
                {isNative && (
                  <button
                    type="button"
                    onClick={pickFromContacts}
                    className="mb-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#EDE7FB] px-4 py-3 font-semibold text-[#7C52F5] active:scale-[0.99] transition-transform"
                  >
                    <UserPlus size={18} aria-hidden="true" />
                    Pick from contacts
                  </button>
                )}
                <label htmlFor="ec-name" className="mb-1.5 block px-1 text-xs font-medium text-[#6B5FA0]">
                  Name
                </label>
                <input
                  id="ec-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => {
                    setError(null);
                    setName(e.target.value);
                  }}
                  placeholder="e.g. Taylor"
                  className="mb-3 w-full rounded-2xl border border-[#E0D8F3] bg-white px-4 py-3.5 text-base text-[#2A2342] placeholder:text-[#A89FC4] focus:border-[#7C52F5] focus:outline-none focus:ring-2 focus:ring-[#7C52F5]/20"
                />
                <PhoneCountryField
                  country={country}
                  nationalNumber={phone}
                  onCountryChange={setCountry}
                  onNationalNumberChange={(v) => {
                    setError(null);
                    setPhone(v);
                  }}
                  label="Phone number"
                  placeholder="(555) 123-4567"
                  classNames={{
                    label: "mb-1.5 block px-1 text-xs font-medium text-[#6B5FA0]",
                    dialBox:
                      "relative flex items-center rounded-2xl border border-[#E0D8F3] bg-white px-3",
                    input:
                      "min-w-0 flex-1 rounded-2xl border border-[#E0D8F3] bg-white px-4 py-3.5 text-base text-[#2A2342] placeholder:text-[#A89FC4] focus:border-[#7C52F5] focus:outline-none focus:ring-2 focus:ring-[#7C52F5]/20",
                    preview: "mt-1.5 px-1 text-xs text-[#6B5FA0]",
                  }}
                />
                {error && (
                  <p className="mt-2 px-1 text-sm text-[#DC2626]" role="alert">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  onClick={goToRelation}
                  className="mt-5 h-[52px] w-full rounded-2xl bg-[#7C52F5] text-base font-semibold text-white active:scale-[0.98] transition-transform"
                >
                  Next
                </button>
              </>
            )}

            {stage === "relation" && (
              <>
                <div className="flex flex-wrap gap-2">
                  {PRESET_RELATIONS.map((r) => {
                    const selected = relationChoice === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setRelationChoice(r)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                          selected
                            ? "bg-[#7C52F5] text-white"
                            : "border border-[#E0D8F3] bg-white text-[#2A2342]"
                        }`}
                      >
                        {r}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    aria-pressed={relationChoice === CUSTOM_RELATION_LABEL}
                    onClick={() => setRelationChoice(CUSTOM_RELATION_LABEL)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                      relationChoice === CUSTOM_RELATION_LABEL
                        ? "bg-[#7C52F5] text-white"
                        : "border border-[#E0D8F3] bg-white text-[#2A2342]"
                    }`}
                  >
                    {CUSTOM_RELATION_LABEL}
                  </button>
                </div>

                {relationChoice === CUSTOM_RELATION_LABEL && (
                  <input
                    type="text"
                    value={customRelation}
                    onChange={(e) => setCustomRelation(e.target.value)}
                    maxLength={30}
                    placeholder="Type a relation"
                    className="mt-4 w-full rounded-2xl border border-[#E0D8F3] bg-white px-4 py-3.5 text-base text-[#2A2342] placeholder:text-[#A89FC4] focus:border-[#7C52F5] focus:outline-none focus:ring-2 focus:ring-[#7C52F5]/20"
                  />
                )}

                {error && (
                  <p className="mt-3 px-1 text-sm text-[#DC2626]" role="alert">
                    {error}
                  </p>
                )}

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStage("entry")}
                    disabled={saving}
                    className="h-[52px] flex-1 rounded-2xl border border-[#E0D8F3] bg-white text-base font-semibold text-[#6B5FA0] active:scale-[0.98] transition-transform disabled:opacity-60"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={confirmAdd}
                    disabled={saving || relationChoice === null}
                    className="h-[52px] flex-[2] rounded-2xl bg-[#7C52F5] text-base font-semibold text-white active:scale-[0.98] transition-transform disabled:opacity-50"
                  >
                    {saving ? "Adding…" : "Add to my circle"}
                  </button>
                </div>
                <p className="mt-3 text-center text-xs text-[#A89FC4]">
                  Relation is just a label — it helps you remember who's who.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </OnboardingShell>
  );
}
